import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { useGetCollaborationQuery } from '@/redux/marketerApi'
import { MainLayout } from '@/components/layout/MainLayout'
import { collabPath } from '@/lib/collabPath'

const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  youtube: 'YouTube',
}

const PLATFORM_CODE_LABELS: Record<string, string> = {
  tiktok: 'TikTok Spark Ads Code',
  instagram: 'Instagram Branded Content Code',
  youtube: 'YouTube Whitelisting',
}

const CollaborationLicensing = () => {
  const { campaignId, collaborationId } = useParams()
  const navigate = useNavigate()

  const { data: collaboration, isLoading } = useGetCollaborationQuery(collaborationId!, {
    skip: !collaborationId,
  })

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
        </div>
      </MainLayout>
    )
  }

  if (!collaboration) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto p-6">
          <p className="text-gray-600">Collaboration not found.</p>
        </div>
      </MainLayout>
    )
  }

  const licensingMilestone = collaboration.milestones.find((m) => m.slug === 'license_content')
  const licensingOffer = collaboration.offerDetails?.licensing_offer

  if (!licensingMilestone && !licensingOffer) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Button
            variant="ghost"
            className="mb-6 text-gray-700 hover:text-foreground hover:bg-foreground/10"
            onClick={() => navigate(collabPath(campaignId, collaborationId!))}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Collaboration
          </Button>
          <Card variant="dark">
            <CardContent className="pt-6">
              <p className="text-center text-gray-600">
                Licensing is not enabled for this collaboration.
              </p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    )
  }

  const platforms = licensingMilestone?.metadata?.platforms ?? []
  const milestoneStep = licensingMilestone?.metadata?.step
  const milestoneStatus = licensingMilestone?.status

  const isCompleted = milestoneStatus === 'completed' || milestoneStatus === 'approved'
  const isAwaitingApproval = milestoneStep === 'pending_approval'
  const isAwaitingCodes = milestoneStep === 'ad_code_submission' && !isCompleted

  const athleteName = collaboration.athlete?.name ?? 'Athlete'

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto p-6">
        <Button
          variant="ghost"
          className="mb-6 text-gray-700 hover:text-foreground hover:bg-foreground/10"
          onClick={() => navigate(collabPath(campaignId, collaborationId!))}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Collaboration
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">Content Licensing</h1>
          <p className="text-gray-600 mt-1">
            {collaboration.campaign?.title ?? 'Campaign'} · {athleteName}
          </p>
        </div>

        {/* Status banner */}
        <div className="mb-6">
          {isCompleted && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div>
                <p className="font-medium text-green-700">Licensing complete</p>
                <p className="text-sm text-green-600/80">
                  {athleteName} has submitted all required platform codes.
                </p>
              </div>
            </div>
          )}
          {isAwaitingApproval && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <Clock className="h-5 w-5 text-yellow-600 shrink-0" />
              <div>
                <p className="font-medium text-yellow-700">Awaiting athlete approval</p>
                <p className="text-sm text-yellow-600/80">
                  {athleteName} needs to approve the licensing request before submitting codes.
                </p>
              </div>
            </div>
          )}
          {isAwaitingCodes && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <Clock className="h-5 w-5 text-blue-600 shrink-0" />
              <div>
                <p className="font-medium text-blue-700">Awaiting platform codes</p>
                <p className="text-sm text-blue-600/80">
                  {athleteName} has approved the request and is submitting authorization codes.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Offer summary */}
        {licensingOffer && (
          <Card variant="dark" className="mb-6">
            <CardHeader>
              <CardTitle className="text-base text-foreground">Licensing Offer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {licensingOffer.platforms.map((p, i) => (
                <div
                  key={i}
                  className="flex justify-between items-start border-b border-foreground/10 pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="font-medium text-foreground">
                      {PLATFORM_LABELS[p.platform] ?? p.platform}
                    </p>
                    <p className="text-sm text-gray-600">
                      {p.territory} · {p.usage_term_days} days
                      {p.usage_permission?.length > 0 && ` · ${p.usage_permission.join(', ')}`}
                    </p>
                  </div>
                  {p.fee > 0 && (
                    <span className="font-semibold text-foreground">${p.fee.toFixed(2)}</span>
                  )}
                </div>
              ))}
              {licensingOffer.total_amount > 0 && (
                <div className="flex justify-between items-center pt-2 border-t border-foreground/10 font-semibold text-foreground">
                  <span>Total</span>
                  <span>${licensingOffer.total_amount.toFixed(2)}</span>
                </div>
              )}
              {licensingOffer.message && (
                <p className="text-sm text-gray-600 italic border-t border-foreground/10 pt-3">
                  "{licensingOffer.message}"
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Platform submission statuses */}
        {platforms.length > 0 && (
          <Card variant="dark">
            <CardHeader>
              <CardTitle className="text-base text-foreground">Platform Submissions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {platforms.map((p) => {
                const label = PLATFORM_LABELS[p.platform] ?? p.platform
                const codeLabel = PLATFORM_CODE_LABELS[p.platform] ?? 'Authorization'
                const isCodeType = p.submission_type === 'code'
                const isDone = p.status === 'completed'

                return (
                  <div
                    key={p.platform}
                    className={`rounded-lg border p-4 space-y-3 ${
                      isDone
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-card border-foreground/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{label}</span>
                      {isDone ? (
                        <Badge className="bg-green-500/20 text-green-700 hover:bg-green-500/20 border-0">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Submitted
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-foreground/20 text-gray-600">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>

                    {isDone && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                          {isCodeType ? codeLabel : 'Confirmation'}
                        </p>
                        {isCodeType && typeof p.submitted_value === 'string' ? (
                          <p className="text-sm font-mono bg-card border border-foreground/10 text-gray-800 rounded px-3 py-2 break-all select-all">
                            {p.submitted_value}
                          </p>
                        ) : (
                          <p className="text-sm text-green-600">Athlete confirmed ✓</p>
                        )}
                        {p.submitted_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Submitted{' '}
                            {new Intl.DateTimeFormat('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            }).format(new Date(p.submitted_at))}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  )
}

export default CollaborationLicensing
